import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  ColorInput,
  Group,
  Menu,
  Modal,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBell,
  IconCheck,
  IconDotsVertical,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconStar,
  IconTextSize,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  richTextPresetsApi,
  RichTextPreset,
  RichTextPresetStyle,
} from '@/services/rich-text-presets.api';
import {
  FONT_FAMILIES,
  matchFontFamily,
  withCurrentValue,
} from '@/features/editor/components/PageElements/ScriptEditor/fontControls';

const DEFAULT_STYLE: RichTextPresetStyle = {
  fontFamily: 'Helvetica',
  fontSize: '14px',
  color: '#000000',
  backgroundColor: '',
  bold: false,
  italic: false,
  underline: false,
  blockType: 'paragraph',
  boxEnabled: false,
  boxBackgroundColor: '#BDE5F8',
  boxBorderColor: '#2B6CB0',
  boxTextColor: '#1B5F9E',
  boxPadding: '16px',
  boxIconType: 'vector',
  boxIcon: 'info',
  boxIconEmoji: '💬',
  boxIconColor: '#FFFFFF',
  calloutTone: 'info',
};

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
const BOX_PADDING = ['8px', '12px', '16px', '20px', '24px'];
const CALLOUT_TONES: Record<'info' | 'warning' | 'note', Partial<RichTextPresetStyle>> = {
  info: {
    calloutTone: 'info',
    boxBackgroundColor: '#BDE5F8',
    boxBorderColor: '#2B6CB0',
    boxTextColor: '#1B5F9E',
    boxIconType: 'vector',
    boxIcon: 'info',
    boxIconColor: '#FFFFFF',
  },
  warning: {
    calloutTone: 'warning',
    boxBackgroundColor: '#FFF0B3',
    boxBorderColor: '#C47A00',
    boxTextColor: '#9A5B00',
    boxIconType: 'vector',
    boxIcon: 'warning',
    boxIconColor: '#FFFFFF',
  },
  note: {
    calloutTone: 'note',
    boxBackgroundColor: '#E9FCEB',
    boxBorderColor: '#2F9E44',
    boxTextColor: '#1F7A33',
    boxIconType: 'vector',
    boxIcon: 'exclamation',
    boxIconColor: '#FFFFFF',
  },
};

const VECTOR_ICON_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'exclamation', label: 'Exclamation' },
  { value: 'check', label: 'Check' },
  { value: 'bell', label: 'Bell' },
  { value: 'star', label: 'Star' },
];

function getMutationErrorMessage(error: any) {
  const responseMessage = error?.response?.data?.message;
  if (Array.isArray(responseMessage)) return responseMessage.join(' ');
  if (typeof responseMessage === 'string') return responseMessage;
  if (error?.response?.status === 409) return 'A preset with that name already exists.';
  if (error?.response?.status >= 500) return 'The server could not save this preset. Check that the latest database migration has been applied.';
  return error?.message || 'Failed to save rich text preset.';
}

export default function RichTextPresetsPage() {
  const queryClient = useQueryClient();
  const [modalOpened, setModalOpened] = useState(false);
  const [editingPreset, setEditingPreset] = useState<RichTextPreset | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    style: DEFAULT_STYLE,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showAdvancedCallout, setShowAdvancedCallout] = useState(false);

  const { data: presets = [], isLoading } = useQuery({
    queryKey: ['rich-text-presets'],
    queryFn: richTextPresetsApi.getPresets,
  });

  const createMutation = useMutation({
    mutationFn: richTextPresetsApi.createPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rich-text-presets'] });
      closeModal();
    },
    onError: (error: any) => {
      setFormError(getMutationErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof richTextPresetsApi.updatePreset>[1] }) =>
      richTextPresetsApi.updatePreset(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rich-text-presets'] });
      closeModal();
    },
    onError: (error: any) => {
      setFormError(getMutationErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: richTextPresetsApi.deletePreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rich-text-presets'] }),
  });

  const openCreate = () => {
    setEditingPreset(null);
    setForm({ name: '', description: '', style: DEFAULT_STYLE });
    setFormError(null);
    setShowAdvancedCallout(false);
    setModalOpened(true);
  };

  const openEdit = (preset: RichTextPreset) => {
    setEditingPreset(preset);
    setForm({
      name: preset.name,
      description: preset.description || '',
      style: { ...DEFAULT_STYLE, ...preset.style },
    });
    setFormError(null);
    setShowAdvancedCallout(!!preset.style.boxEnabled && preset.style.calloutTone === 'custom');
    setModalOpened(true);
  };

  const closeModal = () => {
    setModalOpened(false);
    setEditingPreset(null);
    setFormError(null);
  };

  const updateStyle = (style: Partial<RichTextPresetStyle>) => {
    setForm((current) => ({
      ...current,
      style: { ...current.style, ...style },
    }));
  };

  const setPresetType = (type: 'text' | 'callout') => {
    if (type === 'text') {
      updateStyle({
        boxEnabled: false,
        color: form.style.boxTextColor || form.style.color,
      });
      return;
    }

    updateStyle({
      ...CALLOUT_TONES.info,
      boxEnabled: true,
      color: undefined,
      backgroundColor: undefined,
    });
  };

  const setCalloutTone = (tone: RichTextPresetStyle['calloutTone']) => {
    if (!tone || tone === 'custom') {
      updateStyle({ calloutTone: 'custom' });
      setShowAdvancedCallout(true);
      return;
    }

    setShowAdvancedCallout(false);
    updateStyle(CALLOUT_TONES[tone]);
  };

  const updateVisibleTextColor = (color: string) => {
    if (form.style.boxEnabled) {
      updateStyle({ boxTextColor: color });
    } else {
      updateStyle({ color });
    }
  };

  const handleSubmit = () => {
    const name = form.name.trim();
    setFormError(null);
    if (!name) {
      setFormError('Preset name is required.');
      return;
    }

    const payload = {
      name,
      description: form.description.trim() || undefined,
      style: form.style,
    };

    if (editingPreset) {
      updateMutation.mutate({ id: editingPreset.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Box p="xl" px={32}>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={2} c="#2A3140">Rich Text Presets</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Reusable typography styles available in every rich text editor.
          </Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />} style={{ backgroundColor: '#415A91' }} radius="md" onClick={openCreate}>
          Add Preset
        </Button>
      </Group>

      {presets.length === 0 && !isLoading ? (
        <Paper p="xl" radius="md" withBorder ta="center">
          <IconTextSize size={48} style={{ color: '#ADB5BD' }} />
          <Text size="lg" fw={500} mt="md" c="#2A3140">No presets yet</Text>
          <Text size="sm" c="dimmed" mt={4}>
            Create text styles your team can reuse across every flow.
          </Text>
          <Button mt="lg" variant="light" color="blue" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Create your first preset
          </Button>
        </Paper>
      ) : (
        <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Preview</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th w={80}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {presets.map((preset) => (
                <Table.Tr key={preset.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{preset.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <PresetPreview style={preset.style} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>{preset.description || '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" shadow="md">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openEdit(preset)}>
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteMutation.mutate(preset.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingPreset ? 'Edit Rich Text Preset' : 'Create Rich Text Preset'}
        size="lg"
      >
        <Stack gap="md">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {formError}
            </Alert>
          )}
          <TextInput
            label="Preset Name"
            placeholder="Green dash heading"
            value={form.name}
            error={formError && !form.name.trim() ? 'Enter a preset name.' : undefined}
            onChange={(e) => {
              if (formError) setFormError(null);
              setForm({ ...form, name: e.currentTarget.value });
            }}
          />
          <Textarea
            label="Description"
            placeholder="When should this preset be used?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
            rows={2}
          />
          <SegmentedControl
            value={form.style.boxEnabled ? 'callout' : 'text'}
            onChange={(value) => setPresetType(value as 'text' | 'callout')}
            data={[
              { value: 'text', label: 'Text Style' },
              { value: 'callout', label: 'Callout Box' },
            ]}
            fullWidth
          />
          {form.style.boxEnabled && (
            <Select
              label="Callout Type"
              description="Pick a type to set icon and colors automatically."
              data={[
                { value: 'info', label: 'Info' },
                { value: 'warning', label: 'Warning' },
                { value: 'note', label: 'Note' },
                { value: 'custom', label: 'Custom' },
              ]}
              value={form.style.calloutTone || 'info'}
              onChange={(value) => setCalloutTone((value as RichTextPresetStyle['calloutTone']) || 'info')}
            />
          )}
          <Group grow>
            <Select
              label="Font"
              // Presets can hold a font the list does not spell the same way
              // ('arial', 'Arial, sans-serif'); Mantine shows an empty control
              // for any value missing from `data`, hiding the preset's real font.
              data={withCurrentValue(matchFontFamily(form.style.fontFamily), FONT_FAMILIES)}
              value={matchFontFamily(form.style.fontFamily)}
              onChange={(value) => updateStyle({ fontFamily: value || undefined })}
            />
            <Select
              label="Size"
              data={FONT_SIZES}
              value={form.style.fontSize || null}
              onChange={(value) => updateStyle({ fontSize: value || undefined })}
            />
          </Group>
          <ColorInput
            label="Text Color"
            value={(form.style.boxEnabled ? form.style.boxTextColor : form.style.color) || '#000000'}
            onChange={updateVisibleTextColor}
          />
          {!form.style.boxEnabled && (
            <>
              <ColorInput
                label="Highlight"
                value={form.style.backgroundColor || ''}
                onChange={(value) => updateStyle({ backgroundColor: value || undefined })}
              />
              <Select
                label="Block Style"
                data={[
                  { value: 'paragraph', label: 'Paragraph' },
                  { value: 'heading1', label: 'Heading 1' },
                  { value: 'heading2', label: 'Heading 2' },
                  { value: 'quote', label: 'Quote' },
                ]}
                value={form.style.blockType || 'paragraph'}
                onChange={(value) => updateStyle({ blockType: (value as RichTextPresetStyle['blockType']) || 'paragraph' })}
              />
            </>
          )}
          <Group>
            <Checkbox label="Bold" checked={!!form.style.bold} onChange={(e) => updateStyle({ bold: e.currentTarget.checked })} />
            <Checkbox label="Italic" checked={!!form.style.italic} onChange={(e) => updateStyle({ italic: e.currentTarget.checked })} />
            <Checkbox label="Underline" checked={!!form.style.underline} onChange={(e) => updateStyle({ underline: e.currentTarget.checked })} />
          </Group>
          {form.style.boxEnabled && (
            <Stack gap="md">
              <Checkbox
                label="Show advanced callout styling"
                checked={showAdvancedCallout}
                onChange={(e) => setShowAdvancedCallout(e.currentTarget.checked)}
              />
              {showAdvancedCallout && (
                <>
                  <Group grow>
                    <ColorInput
                      label="Box Background"
                      value={form.style.boxBackgroundColor || '#BDE5F8'}
                      onChange={(value) => updateStyle({ boxBackgroundColor: value, calloutTone: 'custom' })}
                    />
                    <ColorInput
                      label="Box Border"
                      value={form.style.boxBorderColor || '#2B6CB0'}
                      onChange={(value) => updateStyle({ boxBorderColor: value, calloutTone: 'custom' })}
                    />
                  </Group>
                  <Group grow>
                    <Select
                      label="Padding"
                      data={BOX_PADDING}
                      value={form.style.boxPadding || '16px'}
                      onChange={(value) => updateStyle({ boxPadding: value || '16px' })}
                    />
                    <Select
                      label="Icon Style"
                      data={[
                        { value: 'vector', label: 'Vector icon' },
                        { value: 'emoji', label: 'Emoji' },
                      ]}
                      value={form.style.boxIconType || 'vector'}
                      onChange={(value) => updateStyle({ boxIconType: (value as RichTextPresetStyle['boxIconType']) || 'vector', calloutTone: 'custom' })}
                    />
                  </Group>
                  {form.style.boxIconType === 'emoji' ? (
                    <TextInput
                      label="Emoji"
                      placeholder="💬"
                      value={form.style.boxIconEmoji || ''}
                      onChange={(event) => updateStyle({ boxIconEmoji: event.currentTarget.value, calloutTone: 'custom' })}
                    />
                  ) : (
                    <Select
                      label="Vector Icon"
                      data={VECTOR_ICON_OPTIONS}
                      value={form.style.boxIcon || 'info'}
                      onChange={(value) => updateStyle({ boxIcon: (value as RichTextPresetStyle['boxIcon']) || 'none', calloutTone: 'custom' })}
                    />
                  )}
                  <ColorInput
                    label="Icon Badge"
                    value={form.style.boxIconColor || '#FFFFFF'}
                    onChange={(value) => updateStyle({ boxIconColor: value, calloutTone: 'custom' })}
                  />
                </>
              )}
            </Stack>
          )}
          <Box>
            <Text size="sm" fw={500} mb={6}>Preview</Text>
            <PresetPreview style={form.style} full />
          </Box>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeModal}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={createMutation.isPending || updateMutation.isPending}
              style={{ backgroundColor: '#415A91' }}
            >
              {editingPreset ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

function PresetPreview({ style, full = false }: { style: RichTextPresetStyle; full?: boolean }) {
  const icon = getCalloutIcon(style);

  if (style.boxEnabled) {
    return (
      <Box
        p={style.boxPadding || '16px'}
        style={{
          border: `1px solid ${style.boxBorderColor || '#2B6CB0'}`,
          borderRadius: 0,
          backgroundColor: style.boxBackgroundColor || '#BDE5F8',
          maxWidth: full ? '100%' : 420,
          color: style.boxTextColor || style.color,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
        }}
      >
        {icon && (
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: style.boxIconColor || '#FFFFFF',
              color: style.boxTextColor || style.color || '#1B5F9E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Text
          style={{
            fontFamily: style.fontFamily,
            fontSize: style.fontSize,
            color: style.boxTextColor || style.color,
            fontWeight: style.bold ? 700 : 400,
            fontStyle: style.italic ? 'italic' : 'normal',
            textDecoration: style.underline ? 'underline' : 'none',
            lineHeight: 1.35,
          }}
        >
          Sample callout text
        </Text>
      </Box>
    );
  }

  return (
    <Box
      px={full ? 'md' : 'sm'}
      py={full ? 'sm' : 4}
      style={{
        border: '1px solid #E4E7ED',
        borderRadius: 8,
        backgroundColor: '#fff',
        maxWidth: full ? '100%' : 360,
      }}
    >
      <Text
        style={{
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
          color: style.color,
          backgroundColor: style.backgroundColor || 'transparent',
          fontWeight: style.bold ? 700 : 400,
          fontStyle: style.italic ? 'italic' : 'normal',
          textDecoration: style.underline ? 'underline' : 'none',
          display: 'inline',
        }}
      >
        Sample preset text
      </Text>
      {style.blockType && style.blockType !== 'paragraph' && (
        <Badge size="xs" variant="light" ml="sm">{style.blockType}</Badge>
      )}
    </Box>
  );
}

function getCalloutIcon(style: RichTextPresetStyle) {
  if (style.boxIconType === 'emoji') return style.boxIconEmoji || null;
  const icon = style.boxIcon;
  if (!icon || icon === 'none') return null;
  const iconProps = { size: 16, stroke: 2.4 };
  if (icon === 'info') return <IconInfoCircle {...iconProps} />;
  if (icon === 'warning') return <IconAlertTriangle {...iconProps} />;
  if (icon === 'check') return <IconCheck {...iconProps} />;
  if (icon === 'bell') return <IconBell {...iconProps} />;
  if (icon === 'star') return <IconStar {...iconProps} />;
  return <IconAlertCircle {...iconProps} />;
}
